import { useCallback, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { MoreActionsMenu } from '../../MoreActionsMenu';
import { useNavigate } from 'react-router-dom';
import {
    useAppDispatch,
    useAppSelector,
    useAppStore,
    useIsMultiPortfolioTenant,
    useStalePlanRefresh
} from '../../../../common/hooks';
import { mpgetproposaldetail, ProposalDetailData } from '../../../../services/clients';
import { syncAdviceGoals } from '../../../../features/client-goals';
import {
    setEwsMaps,
    setAffectedEwsTerms,
    seedAdviceContinuityForReallocation,
    AdviceContinuityGoal
} from '../../../../features/ews';
import {
    saveClient,
    setEnableConfirmConsoleNavigation,
    syncAdviceClient,
    updateCurrentStep
} from '../../../../features/client';
import { clearUnsavedPlanChanges, shouldKeepPlanEditsInStore } from '../../../../features/unsaved-plan-changes';
import { updateFactsheetModalVisibility, updateGlobalLoaderState } from '../../../../features/global/globalSlice';
import { getDocsByProposalIds } from '../../../../features/advisorConsole';
import {
    API_VERSION,
    FEATURE_NAMES,
    getEwsDurationKeys,
    PARTICIPANT_ID_AVAILABLE_TENANTS,
    STATUSVARIANTS
} from '../../../../common/constants';
import {
    callSelectiveEWS,
    downloadPdfThroughBackend,
    getFileFromStore,
    getClientJourneyFlowType,
    PdfDownloadToastState,
    SetPdfToast,
    formatTimestampToDateInParts,
    buildConsolePdfFileName,
    formatPdfFileDate,
    checkFFEnabled,
    callUnifiedEWS,
    isTenantAdmin,
    isOwnProposal,
    extractRecommendedPortfolioIdByGoal
} from '../../../../utils';
import { useTranslation } from 'react-i18next';
import { ADVISOR_ROUTES } from '../../../../routes/routeConstants';
import { GoalRecommendationType } from 'common/types';
import { Icon, IconButton } from '../../../atoms';
import { Box, Chip, CircularProgress, TableCell, TableRow, Typography as MuiTypography, useTheme } from '@mui/material';
import { ReactComponent as FileDownloadIcon } from '../../../../assets/icons/file_download.svg';
import { ReactComponent as ListAltIcon } from '../../../../assets/icons/list_alt.svg';
import { ReactComponent as DownloadInProgressIcon } from '../../../../assets/icons/downloadInProgress.svg';
import { InfoTooltip } from '../../../../components/molecules/InfoTooltip';
import { PdfDownloadErrorBanner } from '../../../../components/molecules/PdfDownloadErrorBanner/PdfDownloadErrorBanner';
import ReallocateAccountBalancesModal from '../../../../containers/reallocate-account-balances';
import { applyEditedBalances } from '../../../../containers/reallocate-account-balances/applyEditedBalances';
import ReviewGoalsModal from '../../../../containers/review-goals';
import {
    applyReviewDecisions,
    buildUpdatedGoalsAfterReview,
    ReviewGoalsResult
} from '../../../../containers/review-goals/applyReviewDecisions';
import { ActionRequiredContext } from '../ActionRequiredIndicator';
import { DrawerComp } from '../../Drawer/Drawer';

const rowClickExemptedTagNames = ['IMG', 'BUTTON'];

// AEX-3603/AEX-3218: a goal re-dated via the Review Goals modal (Not Realized) only changes its
// investment tenure; other continuity flags stay false and its prior recommended portfolio is dropped.
const RE_DATED_GOAL_CONTINUITY: AdviceContinuityGoal = {
    isNewGoal: false,
    isNewGoalPriority: false,
    isNewInvestmentTenure: true,
    currentPortfolioId: null
};

const ExpandButton = styled('button')<{ isExpanded?: boolean }>(({ isExpanded, theme }) => ({
    border: 'none',
    backgroundColor: theme.palette.semantic.bg.default,
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isExpanded && {
        svg: {
            transform: 'rotate(90deg)'
        }
    })
}));

export const ActionButton = styled('button')(({ theme }) => ({
    background: 'transparent',
    border: 'none',
    margin: 0,
    padding: 0,
    cursor: 'pointer',
    '> svg': { color: theme.palette.semantic.icon.default }
}));

const ProposalPdfContainer = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
}));

const StyledIconButton = styled(IconButton)<{ $isEnabled?: boolean }>(({ theme, $isEnabled }) => ({
    padding: 0,
    '& path': {
        fill: $isEnabled ? theme.palette.primary[600] : theme.palette.grey[300]
    }
}));

const StyledTableRow = styled(TableRow)(() => ({
    height: '64px'
}));

type ColumnAlign = 'left' | 'center' | 'right' | 'inherit' | 'justify';

type ColumnDef = {
    label: string;
    key: string;
    align: ColumnAlign;
    width: string;
    admin?: boolean;
    // eslint-disable-next-line no-unused-vars
    render?: (value: any, row: any) => React.ReactNode;
};

type Props = {
    rowData: any;
    columns: ColumnDef[];
    // eslint-disable-next-line no-unused-vars
    onDeleteClick?(planId: string): void;
    enablePlanDetails?: boolean;
    enablePDFGenerate?: boolean;
    enableReallocate?: boolean;
    // eslint-disable-next-line no-unused-vars
    setPdfToast?: (value: PdfDownloadToastState) => void;
    // eslint-disable-next-line no-unused-vars
    setConsoleErrorBanner?: (planId: string, hasError: boolean) => void;
    pdfErrorPlanIds?: string[];
    isExpanded?: boolean;
    // eslint-disable-next-line no-unused-vars
    toggleRow?: (rowKey: string | undefined | any) => void;
    rowKey: string | number;
    showExpandButton?: boolean;
};

const resolveParticipantId = (...sources: any[]): string => {
    for (const source of sources) {
        const value = source?.participant_id;
        if (value) return value as string;
    }
    return '';
};

const CellUnderlinedButtonText = styled('span')(({ theme }) => ({
    color: theme.palette.semantic.int.selected,
    textAlign: 'center',
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontFamily: 'TT Commons Pro',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '18px' /* 138.462% */,
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    textDecorationSkipInk: 'none',
    textDecorationThickness: 'auto',
    textUnderlineOffset: 'auto',
    textUnderlinePosition: 'from-font'
}));

const Row = ({
    rowData,
    columns,
    onDeleteClick,
    enablePDFGenerate,
    enableReallocate,
    enablePlanDetails,
    setPdfToast,
    setConsoleErrorBanner,
    pdfErrorPlanIds,
    isExpanded,
    toggleRow,
    rowKey,
    showExpandButton = true
}: Props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    // AEX-3363: stale-plan detection + "Refresh Plan" recovery for the Reallocate modal. Controlled here so the
    // Profile & Finances CTA can also flag balance edits that no longer fit the latest plan (AEX-3218).
    const [isReallocateStale, setIsReallocateStale] = useState(false);
    const { markStaleFromError, refreshPlan: refreshReallocatePlan } = useStalePlanRefresh(undefined, {
        isStalePlan: isReallocateStale,
        setIsStalePlan: setIsReallocateStale
    });
    const {
        advisorConsole,
        tenant,
        user,
        adviceGoals: storeAdviceGoals
    } = useAppSelector((state) => ({
        tenant: state?.auth?.user?.tenant,
        advisorConsole: state?.advisorConsole,
        adviceGoals: state?.adviceGoals,
        user: state?.auth?.user
    }));
    const allDocsLoading = useAppSelector((state) => state?.advisorConsole?.allDocsLoading);
    const isFlow2 = getClientJourneyFlowType(tenant) === 'flow2';
    // AEX-3570: flow2 + FF rows with active goal notifications get a left warning accent.
    const showActionRequired =
        checkFFEnabled(FEATURE_NAMES['AEX-3570_NOTIFICATIONS_IN_GETPROPOSALS']) &&
        isFlow2 &&
        (rowData?.actionRequiredGoalCount ?? 0) > 0;
    const downloadInProgressIconStyle = {
        ['--spinner-track']: theme.palette.semantic.bd.subtle,
        ['--spinner-arc']: theme.palette.semantic.icon.brand
    } as React.CSSProperties;

    // const userRole = useAppSelector((state) => state?.auth?.user?.userRole);
    const weightType: 'both' | 'strategic_only' =
        useAppSelector((state) => state?.global?.globalConfig?.tenant_supported_weight_type) || 'both';
    const { orgInfo } = useAppSelector((state) => state.global);
    const globalState = useAppSelector((state) => state.global);
    const goalCategoryPortfolioMappings = useAppSelector(
        (state) => state?.global?.globalConfig?.goalCategoryPortfolioMappings
    );
    const doc = advisorConsole.keyValueRequest[rowData.id];
    const docsByProposalId = advisorConsole.docsByProposalId;
    const currentProposalId = doc?.proposalId;
    const proposalDocs = docsByProposalId?.data?.[currentProposalId];
    const [currentVersion, ...previousVersions] = proposalDocs || [];
    const [pdfDownloadFailed, setPdfDownloadFailed] = useState<'current' | number | false>(false);
    const [drawerPdfDownloadFailed, setDrawerPdfDownloadFailed] = useState<Set<'current' | number>>(new Set());
    const [showDrawerToast, setShowDrawerToast] = useState(false);
    const [isReallocateModalOpen, setIsReallocateModalOpen] = useState(false);
    // AEX-3218: Review Goals (step 1) + whether Reallocate is being shown as step 2 of that flow.
    const [isReviewGoalsOpen, setIsReviewGoalsOpen] = useState(false);
    const [isReallocateStepTwo, setIsReallocateStepTwo] = useState(false);
    // AEX-3218: goalIds re-dated (Not Realized) in Review Goals — their reallocation continuity marks
    // isNewInvestmentTenure so EWS re-recommends a portfolio for the new tenure.
    const [reviewReDatedGoalIds, setReviewReDatedGoalIds] = useState<string[]>([]);
    // AEX-3218: the advisor's Review Goals decisions, committed to the store only on Reallocate submit.
    const [reviewResult, setReviewResult] = useState<ReviewGoalsResult | null>(null);
    // AEX-3218: Profile & Finances from the Reallocate modal is in flight (the modal stays open meanwhile).
    const [isNavigatingToProfile, setIsNavigatingToProfile] = useState(false);
    const isNavigatingToProfileRef = useRef(false);
    const [isPdfVersionHistoryOpen, setIsPdfVersionHistoryOpen] = useState(false);
    const [isCurrentVersionDownloading, setIsCurrentVersionDownloading] = useState(false);
    const [isPreviousVersionDownloading, setIsPreviousVersionDownloading] = useState<number | null>(null);
    const [isSubRowDownloading, setIsSubRowDownloading] = useState(false);
    const [isProposalPdfDownloading, setIsProposalPdfDownloading] = useState(false);
    const shouldDisablePlanDetails = !enablePlanDetails;
    const disablePDFGenerate = !enablePDFGenerate;
    const isTenantAdminAndNotOwnProposal = isTenantAdmin(user) && !isOwnProposal(user, doc);
    const disableReallocate = !enableReallocate || isTenantAdminAndNotOwnProposal;
    const disableDownload = disablePDFGenerate || doc?.global?.status === 'incomplete';
    const hasExpandableRows = !!rowData.subItems?.length;
    const showPdfDownloadError = pdfDownloadFailed === 'current' || !!pdfErrorPlanIds?.includes(rowData.id);
    const isMultiPortfolioTenant = useIsMultiPortfolioTenant();
    // eslint-disable-next-line no-unused-vars
    enum Stage {
        // eslint-disable-next-line no-unused-vars
        createClient = 0,
        // eslint-disable-next-line no-unused-vars
        riskTolerance = 1,
        // eslint-disable-next-line no-unused-vars
        createGoals = 2,
        // eslint-disable-next-line no-unused-vars
        reviewPlan = 3
    }
    // eslint-disable-next-line no-unused-vars
    enum StageFlow2 {
        // eslint-disable-next-line no-unused-vars
        createClient = 0,
        // eslint-disable-next-line no-unused-vars
        riskTolerance = 1,
        // eslint-disable-next-line no-unused-vars
        createGoals = 2,
        // eslint-disable-next-line no-unused-vars
        fundingNavigator = 3,
        // eslint-disable-next-line no-unused-vars
        reviewPlan = 4,
        // eslint-disable-next-line no-unused-vars
        planExecution = 5
    }
    const shouldEnableGoalsStep = doc?.step >= Stage.createGoals || doc?.form_fields?.length > 0;

    const fetchProposalDetail = async (): Promise<ProposalDetailData> => {
        return mpgetproposaldetail({ proposalId: doc.proposalId, tenant: doc.tenant });
    };

    const loadClientToStore = (stage: Stage) => {
        const recommendationArr: GoalRecommendationType[] = [];
        if (!doc.global?.recommendation && doc?.form_fields?.length > 0) {
            doc.form_fields.forEach((goal: any) => {
                recommendationArr.push({
                    goalId: goal.goalId,
                    orginalAmount: goal.goalAmount,
                    oneTimeTopUp: 0,
                    topUpAccumulation: 0,
                    topUpDecumulation: 0,
                    recomendedTenure: 0
                });
            });
        }

        const adviceGoals = {
            _id: doc.proposalId || '',
            apiName: doc.api_name || '',
            tenant: doc.tenant,
            apiVer: doc.api_ver || API_VERSION,
            assignedClientId: doc.assignedClientId,
            formFields: doc.form_fields || [],
            request: doc.request || [],
            response: doc.response || [],
            isMultiGoal: doc.is_multi_goal || false,
            iwsApplied: doc.iws_applied || false,
            da_advice_id: doc?.da_advice_id,
            loading: false,
            error: '',
            goeConfigEmail: doc.goeConfigEmail,
            global: {
                ...doc.global
            },
            infusionindex: {}
        };
        dispatch(syncAdviceGoals(adviceGoals));
        dispatch(clearUnsavedPlanChanges());
        dispatch(setAffectedEwsTerms(getEwsDurationKeys(isMultiPortfolioTenant)));
        dispatch(
            setEwsMaps({
                requests: doc.global?.ewsRequests ?? {},
                responses: doc.global?.ewsResponses ?? {}
            })
        );

        dispatch(
            syncAdviceClient({
                _id: doc._id,
                accounts: {
                    ...doc?.accounts,
                    incomeSources: (doc.accounts?.incomeSources || []).map((source: any) => ({
                        ...source,
                        update: false
                    })),
                    investmentsSources: (doc.accounts?.investmentsSources || []).map((source: any) => ({
                        ...source,
                        update: false
                    }))
                },
                clientDob: doc.clientDob,
                clientFirstName: doc.clientFirstName,
                clientLastName: doc.clientLastName,
                relationship: doc.relationship,
                riskProfile: doc.riskProfile,
                stages: doc.stages,
                step: stage,
                tenant: doc.tenant,
                loading: false,
                hasUnsavedChanges: false,
                enableConfirmConsoleNavigation: false,
                // AEX-1931
                disableConfirmConsoleNavigation: true,
                retirementAge: doc.retirementAge,
                planningAge: doc.planningAge,
                participant_id: resolveParticipantId(doc, currentVersion),
                isParticipantIdAvailable: PARTICIPANT_ID_AVAILABLE_TENANTS.includes(doc?.tenant ?? '')
            })
        );
    };

    // Maps a fetched proposal to the plan (goals + client) the store holds once it is opened; dispatches nothing.
    const buildPlanFromProposal = (
        stage: StageFlow2,
        proposalData: ProposalDetailData,
        // AEX-3363: when opening the editable Profile from the Reallocate modal, seed the client from
        // the freshest data.client_profile (accounts + client_ref_id) instead of the stale console doc.
        sourceClientFromProposal = false,
        // AEX-3218: Review Goals decisions to apply on top of the fetched plan, when the advisor leaves
        // step 2 (Update Balances) through Profile & Finances instead of Reallocate.
        reviewDecisions: ReviewGoalsResult | null = null
    ) => {
        const proposalGoals = {
            _id: proposalData._id || '',
            apiName: proposalData.api_name || '',
            tenant: proposalData.tenant,
            apiVer: proposalData.api_ver || API_VERSION,
            assignedClientId: proposalData.assignedClientId,
            formFields: proposalData.form_fields || [],
            request: proposalData.request || [],
            response: proposalData.response || [],
            isMultiGoal: proposalData.is_multi_goal || false,
            iwsApplied: proposalData.iws_applied || false,
            loading: false,
            error: '',
            goeConfigEmail: proposalData.goeConfigEmail,
            da_advice_id: proposalData?.da_advice_id,
            // AEX-3363: capture the proposal reference id so subsequent updates echo it.
            proposal_ref_id: proposalData.proposal_ref_id,
            global: {
                ...proposalData.global,
                status: STATUSVARIANTS.INCOMPLETE,
                planExecutionResponse: {
                    // reorient from BE root into global as per component usage
                    rebalancerResult: proposalData?.rebalancer_ref_id?.response,
                    rebalancer_ref_id: proposalData?.rebalancer_ref_id?._id
                },
                // Mark a reopened saved proposal with valid Rebalancer data as 'success' so the
                // readiness rule treats it as ready (Save Plan / Proposal & Fact Sheets / Trade
                // Instructions) instead of falling back to the error/not-ready state.
                planExecutionStatus:
                    proposalData?.rebalancer_ref_id?.response &&
                    Object.keys(proposalData.rebalancer_ref_id.response).length > 0
                        ? 'success'
                        : 'idle'
            },
            infusionindex: {}
        };
        // AEX-3218: commit the Review Goals decisions like Reallocate does — realized goals deleted (also from
        // the EWS maps), not-realized goals re-dated, notification acks/goal events kept for Save Plan.
        const adviceGoals = reviewDecisions
            ? applyReviewDecisions(
                  proposalGoals,
                  reviewDecisions,
                  !!checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
              )
            : proposalGoals;
        // AEX-3363: opening the editable Profile from the Reallocate modal seeds the client from the
        // freshest data.client_profile (accounts + client_ref_id); the normal open path keeps sourcing
        // the client from the console doc.
        const clientProfile = proposalData.client_profile;
        const clientSource: any = sourceClientFromProposal && clientProfile ? clientProfile : doc;
        const adviceClient = {
            _id: doc._id,
            accounts: clientSource.accounts,
            clientDob: clientSource.clientDob,
            clientFirstName: clientSource.clientFirstName,
            clientLastName: clientSource.clientLastName,
            relationship: clientSource.relationship,
            riskProfile: clientSource.riskProfile,
            stages: clientSource.stages,
            step: stage,
            tenant: clientSource.tenant ?? doc.tenant,
            loading: false,
            hasUnsavedChanges: false,
            enableConfirmConsoleNavigation: false,
            // AEX-1931
            disableConfirmConsoleNavigation: true,
            retirementAge: clientSource.retirementAge,
            planningAge: clientSource.planningAge,
            participant_id: resolveParticipantId(clientProfile, proposalData, doc, currentVersion),
            isParticipantIdAvailable: PARTICIPANT_ID_AVAILABLE_TENANTS.includes(
                clientSource.tenant ?? doc?.tenant ?? ''
            ),
            // AEX-3363: reference id for stale-plan validation on updateclientprofile.
            client_ref_id: proposalData.client_profile?.client_ref_id
        };
        const reDatedGoalIds = reviewDecisions?.notRealizedUpdates.map((update) => update.goalId) ?? [];
        return { adviceGoals, adviceClient, reDatedGoalIds };
    };

    // Loads a plan from buildPlanFromProposal into the store; `includeClient` is false when a save already
    // synced the client.
    const loadPlanToStore = (
        { adviceGoals, adviceClient, reDatedGoalIds }: ReturnType<typeof buildPlanFromProposal>,
        includeClient = true
    ) => {
        dispatch(syncAdviceGoals(adviceGoals));
        dispatch(clearUnsavedPlanChanges());
        dispatch(setAffectedEwsTerms(getEwsDurationKeys(isMultiPortfolioTenant)));
        dispatch(
            setEwsMaps({
                requests: adviceGoals.global?.ewsRequests ?? {},
                responses: adviceGoals.global?.ewsResponses ?? {}
            })
        );
        dispatch(
            seedAdviceContinuityForReallocation({
                goals: (adviceGoals.formFields ?? []).map((goal: any) => ({
                    goalId: goal.goalId,
                    currentPortfolioId: null,
                    goalContinuityObject: reDatedGoalIds.includes(goal.goalId) ? RE_DATED_GOAL_CONTINUITY : null
                })),
                isPlanSavedOnce: Boolean(doc?.global?.isPlanSavedOnce)
            })
        );
        if (includeClient) dispatch(syncAdviceClient(adviceClient));
    };

    const loadClientToStoreFromProposal = (stage: StageFlow2, proposalData: ProposalDetailData) =>
        loadPlanToStore(buildPlanFromProposal(stage, proposalData));

    const navigateToStage = (stage: Stage) => {
        if (doc) {
            dispatch(updateFactsheetModalVisibility({ showModal: false, showBanner: false, showPdfViewer: false }));
            loadClientToStore(stage);
            if (stage === Stage.createClient) navigate(ADVISOR_ROUTES.CLIENT_PROFILE);
            if (stage === Stage.riskTolerance) navigate(ADVISOR_ROUTES.RISK_TOLERANCE);
            if (stage === Stage.createGoals) navigate(ADVISOR_ROUTES.GOALS);
            if (stage === Stage.reviewPlan) navigate(ADVISOR_ROUTES.PLAN_DETAILS);
        }
    };

    const navigateToStageWithProposal = (
        stage: StageFlow2,
        route: typeof ADVISOR_ROUTES[keyof typeof ADVISOR_ROUTES],
        proposalData: ProposalDetailData
    ) => {
        if (doc) {
            dispatch(updateFactsheetModalVisibility({ showModal: false, showBanner: false, showPdfViewer: false }));
            loadClientToStoreFromProposal(stage, proposalData);
            navigate(route);
        }
    };

    const handleRowClick = (e: any) => {
        if (!rowClickExemptedTagNames.includes(e.target.tagName)) {
            handleOpenPlanClick();
        }
    };

    // AEX-3218: the "Review now" indicator opens the Review Goals modal (step 1) directly; it then
    // advances to Reallocate (step 2). Decoupled from the client-name cell gating.
    const handleReviewNow = () => {
        if (isTenantAdminAndNotOwnProposal) return;
        if (doc && !disableReallocate) {
            loadClientToStore(Stage.reviewPlan);
            setReviewReDatedGoalIds([]);
            setReviewResult(null);
            setIsReviewGoalsOpen(true);
        }
    };

    // Banorte (flow2): completed plans open the Reallocate modal instead of the plan.
    const handleClientNameClick = () => {
        if (isTenantAdminAndNotOwnProposal) {
            return;
        }
        if (isFlow2 && rowData.status === STATUSVARIANTS.COMPLETE) {
            if (doc && !disableReallocate) {
                loadClientToStore(Stage.reviewPlan);
                // AEX-3218: plans with goals needing review open the Review Goals flow (step 1), which
                // then advances to Reallocate (step 2). Other completed plans open Reallocate directly.
                if (showActionRequired) {
                    setIsReviewGoalsOpen(true);
                } else {
                    setReviewReDatedGoalIds([]);
                    setIsReallocateStepTwo(false);
                    setIsReallocateModalOpen(true);
                }
            }
            return;
        }
        handleOpenPlanClick();
    };
    const handleOpenPlanClick = async () => {
        if (isTenantAdminAndNotOwnProposal) return;
        if (isFlow2 && doc?._id && doc?.proposalId) {
            try {
                dispatch(updateGlobalLoaderState(true));
                const proposalData = await fetchProposalDetail();
                if (doc.global.status === STATUSVARIANTS.COMPLETE)
                    navigateToStageWithProposal(StageFlow2.planExecution, ADVISOR_ROUTES.PLAN_EXECUTION, proposalData);
                else {
                    const priorPortfolioByGoal = extractRecommendedPortfolioIdByGoal(doc.global?.ewsResponses ?? {});
                    dispatch(
                        seedAdviceContinuityForReallocation({
                            goals: (doc?.form_fields ?? []).map((goal: any) => ({
                                goalId: goal.goalId,
                                currentPortfolioId: priorPortfolioByGoal[goal.goalId] ?? null
                            })),
                            isPlanSavedOnce: Boolean(doc?.global?.isPlanSavedOnce)
                        })
                    );
                    navigateToStageWithProposal(StageFlow2.createClient, ADVISOR_ROUTES.CLIENT_PROFILE, proposalData);
                }
            } catch (error) {
                console.error('mpgetproposaldetail error:', error);
            } finally {
                dispatch(updateGlobalLoaderState(false));
            }
        } else {
            if (isFlow2 && doc?.global?.status !== STATUSVARIANTS.COMPLETE) {
                navigateToStage(Stage.createClient);
                return;
            }
            if (!shouldDisablePlanDetails) navigateToStage(Stage.reviewPlan);
            else if (shouldEnableGoalsStep) navigateToStage(Stage.createGoals);
            else if (!(doc?.step < Stage.riskTolerance)) navigateToStage(Stage.riskTolerance);
            else navigateToStage(Stage.createClient);
        }
    };

    const closeReallocateModal = () => {
        setIsReallocateModalOpen(false);
        setIsReallocateStepTwo(false);
    };

    // AEX-3218: Profile & Finances from the Reallocate modal keeps what the advisor confirmed there, like Reallocate:
    // the Review Goals decisions (step 2) and the edited balances, applied on top of the latest plan (re-fetched so
    // the Profile edits work on fresh data + ref ids, AEX-3363). The modal stays open until the navigation, and
    // nothing reaches the store unless all of it applies and the balance save succeeds.
    const handleNavigateToProfile = async (editedAccounts: any[]) => {
        if (!doc || isNavigatingToProfileRef.current) return;
        isNavigatingToProfileRef.current = true;
        setIsNavigatingToProfile(true);
        try {
            const reviewDecisions = isReallocateStepTwo ? reviewResult : null;
            const proposalData = await fetchProposalDetail();
            const plan = buildPlanFromProposal(StageFlow2.createClient, proposalData, true, reviewDecisions);
            const investmentsSources = applyEditedBalances(
                plan.adviceClient.accounts?.investmentsSources,
                editedAccounts
            );
            if (!investmentsSources) {
                // An edited account is gone from the latest plan: it changed since the modal was opened.
                setIsReallocateStale(true);
                return;
            }
            const editedClient = {
                ...plan.adviceClient,
                accounts: { ...plan.adviceClient.accounts, investmentsSources }
            };
            const hasEditedBalances = editedAccounts.length > 0;
            // Saved like a balance edit on Profile & Finances. Persisted right away: saved before the plan is
            // loaded (the save syncs the client). Kept in the store until Save Plan: saved after it, against the
            // latest accounts.
            if (hasEditedBalances && !shouldKeepPlanEditsInStore(store.getState())) {
                await dispatch(saveClient(editedClient));
                loadPlanToStore(plan, false);
            } else {
                loadPlanToStore(plan);
                if (hasEditedBalances) await dispatch(saveClient(editedClient));
            }
            // The applied review decisions are unsaved plan changes, like an edited goal.
            if (reviewDecisions) dispatch(setEnableConfirmConsoleNavigation(true));
            closeReallocateModal();
            navigate(ADVISOR_ROUTES.CLIENT_PROFILE);
        } catch (error) {
            // AEX-3363: stale plan — keep the modal open so its StalePlanBanner + Refresh Plan CTA show.
            if (markStaleFromError(error)) return;
            console.error('Profile & Finances navigation error:', error);
            closeReallocateModal();
        } finally {
            isNavigatingToProfileRef.current = false;
            setIsNavigatingToProfile(false);
        }
    };

    const downloadPDFBackend = useCallback(async () => {
        setConsoleErrorBanner?.(rowData.id, false);
        if (setPdfToast) {
            setPdfToast({ open: true, generating: true });
            const toastHandler = (value: PdfDownloadToastState) => {
                setPdfToast(value);
                if (value.type === 'error') {
                    setPdfDownloadFailed('current');
                    setConsoleErrorBanner?.(rowData.id, true);
                } else if (value.type === 'confirm') {
                    setConsoleErrorBanner?.(rowData.id, false);
                }
            };
            await downloadPdfThroughBackend(
                {
                    tenant: doc?.tenant || '',
                    proposalId: doc?.proposalId || '',
                    assignedClientId: doc?._id || '',
                    da_advice_id: doc?.da_advice_id || '',
                    orgEmail: orgInfo?.orgEmail || '',
                    weightType: weightType || '',
                    filename: buildConsolePdfFileName(doc),
                    language: i18n.language
                },
                toastHandler
            );
        }
    }, [doc, rowData.id, setPdfToast, setConsoleErrorBanner]);

    const downloadProposalVersionFile = async (versionDoc: any, versionNumber: number, toastHandler: SetPdfToast) => {
        const filename = `${doc.clientFirstName}_${doc.clientLastName}_${formatPdfFileDate(
            versionDoc?.timestamp
        )}_v${versionNumber}.pdf`;
        if (versionDoc?.file?.filepath) {
            await getFileFromStore(doc.tenant, versionDoc.file.filepath, filename, toastHandler);
        } else {
            await downloadPdfThroughBackend(
                {
                    tenant: doc.tenant,
                    proposalId: currentProposalId || '',
                    assignedClientId: doc.assignedClientId || '',
                    da_advice_id: versionDoc?.da_advice_id || '',
                    orgEmail: orgInfo?.orgEmail || '',
                    weightType: weightType || '',
                    filename,
                    language: i18n.language
                },
                toastHandler
            );
        }
    };

    const handleOpenPdfVersionHistoryClick = () => {
        setIsPdfVersionHistoryOpen(true);
        dispatch(getDocsByProposalIds({ tenant: doc.tenant, proposalIds: [doc.proposalId] }));
    };

    const isPlanSavedOnce = Boolean(doc?.global?.isPlanSavedOnce);

    let MoreActionsMenuOptions = [
        {
            label: isFlow2 ? t('PLAN_PDF_VERSION_HISTORY') : t('OPEN_PLAN'),
            //icon: 'plan-details',
            disabled: isFlow2 ? !isPlanSavedOnce : isTenantAdminAndNotOwnProposal,
            onClick: isFlow2 ? handleOpenPdfVersionHistoryClick : handleOpenPlanClick
        },
        {
            label: t('DELETE_PLAN_TITLE'),
            //icon: 'delete',
            disabled: isTenantAdminAndNotOwnProposal,
            onClick: () => onDeleteClick && onDeleteClick(rowData.id)
        }
    ];

    return (
        <ActionRequiredContext.Provider value={{ onReviewNow: handleReviewNow }}>
            {/* AEX-3218: stay mounted through step 2 (hidden) so Back retains the advisor's selections;
                closing from either step unmounts it, which clears the selections. */}
            {(isReviewGoalsOpen || isReallocateStepTwo) && (
                <ReviewGoalsModal
                    isOpen={isReviewGoalsOpen}
                    onClose={() => setIsReviewGoalsOpen(false)}
                    goals={rowData.actionRequiredGoals ?? []}
                    onNext={(result) => {
                        // AEX-3218: capture the review decisions and advance to Reallocate (step 2). They are
                        // committed to the store only when Reallocate is clicked (see onSubmit), so Back or
                        // closing the modal leaves the store untouched.
                        setReviewResult(result);
                        setReviewReDatedGoalIds(result.notRealizedUpdates.map((update) => update.goalId));
                        setIsReviewGoalsOpen(false);
                        setIsReallocateStepTwo(true);
                        setIsReallocateModalOpen(true);
                    }}
                />
            )}
            {isReallocateModalOpen && (
                <ReallocateAccountBalancesModal
                    isOpen={isReallocateModalOpen}
                    onClose={closeReallocateModal}
                    isStale={isReallocateStale}
                    onRefreshPlan={refreshReallocatePlan}
                    isStepTwo={isReallocateStepTwo}
                    onBack={() => {
                        setIsReallocateModalOpen(false);
                        setIsReallocateStepTwo(false);
                        setIsReviewGoalsOpen(true);
                    }}
                    onSubmit={async (isEdited: boolean, values: any) => {
                        // AEX-3218: the review flow (step 2) proceeds even with no balance edit so the goal
                        // changes re-run EWS and carry forward; standalone reallocate still no-ops.
                        if (!doc || (!isEdited && !isReallocateStepTwo)) {
                            setIsReallocateModalOpen(false);
                            return;
                        }
                        // Pick only client keys off the merged console `doc`; spreading `...doc`
                        // leaks proposal keys (form_fields/request/response/global/etc.) into the
                        // /updateclientprofile payload. Mirrors loadClientToStore's client shape.
                        const updatedAdviceClient = {
                            _id: doc._id,
                            accounts: {
                                ...doc.accounts,
                                investmentsSources: values.accounts
                            },
                            clientDob: doc.clientDob,
                            clientFirstName: doc.clientFirstName,
                            clientLastName: doc.clientLastName,
                            relationship: doc.relationship,
                            riskProfile: doc.riskProfile,
                            stages: doc.stages,
                            step: doc.step,
                            tenant: doc.tenant,
                            retirementAge: doc.retirementAge,
                            planningAge: doc.planningAge,
                            participant_id: resolveParticipantId(doc, currentVersion),
                            isParticipantIdAvailable: PARTICIPANT_ID_AVAILABLE_TENANTS.includes(doc?.tenant ?? '')
                        };
                        if (isEdited) {
                            try {
                                // Persist the edited balances first. On a stale-plan reject this throws
                                // before the proposal/EWS legs run (AEX-3363), letting us surface the
                                // banner and keep the modal open instead of failing silently.
                                await dispatch(saveClient(updatedAdviceClient));
                            } catch (e) {
                                // Stale plan: keep the modal open so its StalePlanBanner + Refresh Plan CTA show.
                                if (markStaleFromError(e)) return;
                                setIsReallocateModalOpen(false);
                                throw e;
                            }
                        }
                        // AEX-3218: commit the review decisions to the (untouched) store goals now — on the
                        // Reallocate click — so realized deletions / not-realized date changes apply only here;
                        // advancing to or backing out of step 2 never mutates the store.
                        dispatch(setAffectedEwsTerms(getEwsDurationKeys(isMultiPortfolioTenant)));
                        const ff3021 = !!checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021']);
                        const reviewedGoals =
                            isReallocateStepTwo && reviewResult
                                ? buildUpdatedGoalsAfterReview(storeAdviceGoals, reviewResult, ff3021)
                                : storeAdviceGoals;
                        const updatedAdviceGoals = {
                            _id: doc.proposalId || '',
                            apiName: doc.api_name || '',
                            tenant: doc.tenant,
                            apiVer: doc.api_ver || API_VERSION,
                            assignedClientId: doc.assignedClientId,
                            formFields: reviewedGoals?.formFields ?? doc.form_fields ?? [],
                            request: reviewedGoals?.request ?? doc.request ?? [],
                            response: reviewedGoals?.response ?? doc.response ?? [],
                            isMultiGoal: doc.is_multi_goal || false,
                            iwsApplied: doc.iws_applied || false,
                            loading: false,
                            error: '',
                            goeConfigEmail: doc.goeConfigEmail,
                            global: {
                                ...doc.global,
                                status: STATUSVARIANTS.INCOMPLETE
                            },
                            infusionindex: {},
                            events: reviewResult?.eventUpdates ?? [],
                            notifications: reviewResult?.notificationUpdates ?? []
                        };
                        dispatch(setAffectedEwsTerms(['shortTerm', 'mediumTerm', 'longTerm']));
                        await dispatch(syncAdviceGoals(updatedAdviceGoals));

                        // AEX-3603: seed continuity so the reallocation EWS payload carries
                        // reallocate=true and each existing goal's prior recommended portfolio.
                        if (checkFFEnabled(FEATURE_NAMES['CONTINUITY_OF_ADVICE_FE_AEX-3603'])) {
                            const priorPortfolioByGoal = extractRecommendedPortfolioIdByGoal(
                                doc.global?.ewsResponses ?? {}
                            );

                            dispatch(
                                seedAdviceContinuityForReallocation({
                                    goals: (updatedAdviceGoals.formFields ?? []).map((goal: any) => {
                                        const isReDated = reviewReDatedGoalIds.includes(goal.goalId);
                                        return {
                                            goalId: goal.goalId,
                                            currentPortfolioId: isReDated
                                                ? null
                                                : priorPortfolioByGoal[goal.goalId] ?? null,
                                            goalContinuityObject: isReDated ? RE_DATED_GOAL_CONTINUITY : null
                                        };
                                    }),
                                    isPlanSavedOnce: Boolean(doc?.global?.isPlanSavedOnce)
                                })
                            );
                        }

                        if (checkFFEnabled(FEATURE_NAMES['FUNDINGNAV_EWS_STREAMING_FE_AEX-3495'])) {
                            callUnifiedEWS(
                                dispatch,
                                globalState?.orgInfo?.incomeSourceType?.en,
                                globalState?.orgInfo?.investmentAccountType?.en,
                                updatedAdviceClient,
                                updatedAdviceGoals,
                                globalState?.orgInfo?.orgEmail || '',
                                tenant,
                                goalCategoryPortfolioMappings,
                                globalState?.goeConfig?.reallocSchedules
                            );
                        } else {
                            callSelectiveEWS(
                                dispatch,
                                globalState?.orgInfo?.incomeSourceType?.en,
                                globalState?.orgInfo?.investmentAccountType?.en,
                                updatedAdviceClient,
                                updatedAdviceGoals,
                                globalState?.orgInfo?.orgEmail || '',
                                tenant,
                                goalCategoryPortfolioMappings,
                                globalState?.goeConfig?.reallocSchedules
                            );
                        }
                        await dispatch(updateCurrentStep(3));
                        setIsReallocateModalOpen(false);
                        navigate(ADVISOR_ROUTES.FUNDING_NAVIGATOR);
                    }}
                    investmentAccounts={doc?.accounts?.investmentsSources || []}
                    onNavigateToProfile={handleNavigateToProfile}
                    isBusy={isNavigatingToProfile}
                />
            )}
            <StyledTableRow
                onClick={showExpandButton ? handleRowClick : undefined}
                key={rowKey}
                sx={{
                    ...(showExpandButton ? { cursor: 'pointer' } : {}),
                    backgroundColor: showExpandButton ? theme.palette.semantic.bg.brandLight1 : 'inherit'
                }}
            >
                {showExpandButton && (
                    <TableCell sx={{ width: '48px', verticalAlign: 'middle' }}>
                        {hasExpandableRows && (
                            <ExpandButton
                                isExpanded={isExpanded}
                                onClick={(e) => {
                                    // stopPropagation: previously the row's tagName check
                                    // (`rowClickExemptedTagNames`) prevented navigation when
                                    // the click target was an <img>. Now that we render an
                                    // <svg> via <Icon>, we explicitly stop the bubble.
                                    e.stopPropagation();
                                    if (toggleRow) toggleRow(rowKey);
                                }}
                            >
                                <Icon id="expander" size={21} />
                            </ExpandButton>
                        )}
                    </TableCell>
                )}

                {columns.map((header, index) => {
                    if (header.key === 'proposalPDF') {
                        return (
                            <TableCell
                                key={header.key}
                                align={header.align}
                                sx={{ width: header.width, verticalAlign: 'middle' }}
                            >
                                <ProposalPdfContainer>
                                    {isProposalPdfDownloading ? (
                                        <DownloadInProgressIcon style={downloadInProgressIconStyle} />
                                    ) : (
                                        <>
                                            {showPdfDownloadError && (
                                                <Box
                                                    sx={{
                                                        width: '14px',
                                                        height: '14px',
                                                        borderRadius: '50%',
                                                        backgroundColor: theme.palette.semantic.bd.error,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <MuiTypography
                                                        sx={{
                                                            color: '#fff',
                                                            fontSize: '12px',
                                                            fontWeight: 700,
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        !
                                                    </MuiTypography>
                                                </Box>
                                            )}
                                            <StyledIconButton
                                                theme={theme}
                                                $isEnabled={!disablePDFGenerate}
                                                disabled={disablePDFGenerate}
                                                color="primary"
                                                iconName={<FileDownloadIcon />}
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (doc) {
                                                        setIsProposalPdfDownloading(true);
                                                        setPdfDownloadFailed(false);
                                                        await downloadPDFBackend();
                                                        setIsProposalPdfDownloading(false);
                                                    }
                                                }}
                                            />
                                        </>
                                    )}
                                </ProposalPdfContainer>
                            </TableCell>
                        );
                    }
                    if (header.key === 'actions') {
                        return (
                            <TableCell
                                key={header.key}
                                align={header.align}
                                sx={{ width: header.width, verticalAlign: 'middle' }}
                            >
                                <div
                                    style={
                                        isFlow2 || (isFlow2 && isMultiPortfolioTenant)
                                            ? {
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  gap: '21px',
                                                  alignItems: 'center'
                                              }
                                            : { display: 'flex', justifyContent: 'center', gap: '36px' }
                                    }
                                >
                                    {!showExpandButton && (
                                        <>
                                            <InfoTooltip
                                                message={t('INFO_TOOLTIP_DOWNLOAD_CURRENT_PDF')}
                                                placement="bottom"
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: disableDownload ? 'not-allowed' : 'pointer'
                                                    }}
                                                >
                                                    {isSubRowDownloading ? (
                                                        <DownloadInProgressIcon style={downloadInProgressIconStyle} />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                '& path': {
                                                                    fill: disableDownload
                                                                        ? theme.palette.grey[300]
                                                                        : theme.palette.semantic.bg.brand
                                                                }
                                                            }}
                                                        >
                                                            {showPdfDownloadError && (
                                                                <Box
                                                                    sx={{
                                                                        width: '14px',
                                                                        height: '14px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor:
                                                                            theme.palette.semantic.bd.error,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0
                                                                    }}
                                                                >
                                                                    <MuiTypography
                                                                        sx={{
                                                                            color: '#fff',
                                                                            fontSize: '12px',
                                                                            fontWeight: 700,
                                                                            lineHeight: 1
                                                                        }}
                                                                    >
                                                                        !
                                                                    </MuiTypography>
                                                                </Box>
                                                            )}
                                                            <ActionButton
                                                                type="button"
                                                                aria-label={t('INFO_TOOLTIP_DOWNLOAD_CURRENT_PDF')}
                                                                aria-disabled={disableDownload}
                                                                style={{
                                                                    cursor: disableDownload ? 'not-allowed' : 'pointer'
                                                                }}
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    if (doc && !disableDownload) {
                                                                        setIsSubRowDownloading(true);
                                                                        setPdfDownloadFailed(false);
                                                                        await downloadPDFBackend();
                                                                        setIsSubRowDownloading(false);
                                                                    }
                                                                }}
                                                            >
                                                                {isFlow2 || (isFlow2 && isMultiPortfolioTenant) ? (
                                                                    <CellUnderlinedButtonText>
                                                                        {t('CELL_BUTTON_DOWNLOAD')}
                                                                    </CellUnderlinedButtonText>
                                                                ) : (
                                                                    <FileDownloadIcon />
                                                                )}
                                                            </ActionButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </InfoTooltip>{' '}
                                            {(isFlow2 || (isFlow2 && isMultiPortfolioTenant)) && (
                                                <InfoTooltip
                                                    message={t('INFO_TOOLTIP_REALLOCATE_PLAN')}
                                                    placement="bottom"
                                                >
                                                    <Box
                                                        component="button"
                                                        type="button"
                                                        aria-label={t('INFO_TOOLTIP_REALLOCATE_PLAN')}
                                                        aria-disabled={disableReallocate}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column-reverse',
                                                            justifyContent: 'center',
                                                            gap: '2px',
                                                            padding: 0,
                                                            border: 'none',
                                                            background: 'transparent',
                                                            cursor: disableReallocate ? 'not-allowed' : 'pointer',
                                                            '& path': {
                                                                fill: disableReallocate
                                                                    ? theme.palette.grey[300]
                                                                    : theme.palette.semantic.bg.brand
                                                            }
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (doc && !disableReallocate) {
                                                                loadClientToStore(Stage.reviewPlan);
                                                                setReviewReDatedGoalIds([]);
                                                                setIsReallocateStepTwo(false);
                                                                setIsReallocateModalOpen(true);
                                                            }
                                                        }}
                                                    >
                                                        <CellUnderlinedButtonText>
                                                            {t('CELL_BUTTON_UPDATE')}
                                                        </CellUnderlinedButtonText>
                                                    </Box>
                                                </InfoTooltip>
                                            )}
                                        </>
                                    )}
                                    <MoreActionsMenu
                                        menuWidth={225}
                                        zIndex={1}
                                        options={MoreActionsMenuOptions}
                                        showExpandButton={showExpandButton}
                                    />
                                </div>
                            </TableCell>
                        );
                    }
                    return (
                        <TableCell
                            key={header.key}
                            align={header.align}
                            sx={{
                                width: header.width,
                                verticalAlign: 'middle',
                                textAlign: header.align,
                                color: theme.palette.semantic.text.default,
                                fontFeatureSettings: "'liga' off",

                                /* Typescale/Normal/text-base.normal */
                                fontFamily: '"TT Commons Pro"',
                                fontSize: 16,
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: 'var(--Font-Line-height-leading-5, 22px)' /* 137.5% */,

                                ...(showActionRequired &&
                                    index === 0 &&
                                    !showExpandButton && {
                                        // boxShadow (not borderLeft) so the accent survives the table's
                                        // border-collapse and renders at the row's left edge.
                                        boxShadow: `inset 4px 0 0 0 ${theme.palette.semantic.bd.warning}`
                                    }),
                                ...(index === (header.admin ? 1 : 0) &&
                                    !showExpandButton && {
                                        color: theme.palette.neutral[950],
                                        fontFeatureSettings: "'liga' off",
                                        fontFamily: '"TT Commons Pro"',
                                        fontSize: 16,
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: 'var(--Font-Line-height-leading-5, 22px)' /* 137.5% */,
                                        textDecorationStyle: 'solid',
                                        textDecorationSkipInk: 'none',
                                        textDecorationThickness: 'auto',
                                        textUnderlineOffset: 'auto',
                                        textUnderlinePosition: 'from-font',
                                        padding: '12px 8px'
                                    })
                            }}
                        >
                            {index === (header.admin ? 1 : 0) && !showExpandButton ? (
                                <Box
                                    component="button"
                                    type="button"
                                    sx={{
                                        cursor: 'pointer',
                                        padding: 0,
                                        border: 'none',
                                        background: 'transparent',
                                        font: 'inherit',
                                        color: 'inherit',
                                        textAlign: 'left',
                                        textDecoration: 'inherit'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClientNameClick();
                                    }}
                                >
                                    {(header.render
                                        ? header.render(rowData[header.key], rowData)
                                        : rowData?.[header.key] || '') || ''}
                                </Box>
                            ) : (
                                (header.render
                                    ? header.render(rowData[header.key], rowData)
                                    : rowData?.[header.key] || '') || ''
                            )}
                        </TableCell>
                    );
                })}
            </StyledTableRow>
            <DrawerComp
                drawerIcon={<ListAltIcon />}
                header={t('VERSION_HISTORY')}
                subHeading={`${doc?.clientFirstName || ''} ${doc?.clientLastName || ''}`.trim()}
                open={isPdfVersionHistoryOpen}
                handleClose={() => {
                    setIsPdfVersionHistoryOpen(false);
                    setDrawerPdfDownloadFailed(new Set());
                    setShowDrawerToast(false);
                }}
                showStyledRequiredText={false}
                showFooter={false}
            >
                {allDocsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px 0' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            padding: `${doc?.global?.status === STATUSVARIANTS.COMPLETE ? '16px' : '0'} 0`
                        }}
                    >
                        {/* Error Alert */}
                        {showDrawerToast && <PdfDownloadErrorBanner />}

                        {/* Current Version */}
                        <Box
                            sx={{
                                background: `linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), #3D143D`,
                                border: `1px solid ${theme.palette.semantic.bd.brand || '#1976d2'}`,
                                borderRadius: '8px',
                                padding: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box>
                                <MuiTypography
                                    variant="body1"
                                    fontWeight={600}
                                    sx={{ color: theme.palette.semantic.text.default }}
                                >
                                    {`v${proposalDocs?.length ?? 0}`} &mdash;{' '}
                                    {`${formatTimestampToDateInParts(currentVersion?.timestamp).day} ${
                                        formatTimestampToDateInParts(currentVersion?.timestamp).month
                                    } ${formatTimestampToDateInParts(currentVersion?.timestamp).year}`}
                                </MuiTypography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Chip
                                    label={t('LATEST_VERSION_HISTORY_CHIP_LABEL')}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme.palette.semantic.bg.success1,
                                        borderColor: theme.palette.semantic.bd.success,
                                        border: `1px solid ${theme.palette.semantic.bd.success}`,
                                        color: theme.palette.semantic.int.selected
                                    }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {drawerPdfDownloadFailed.has('current') && (
                                        <Box
                                            sx={{
                                                width: '18px',
                                                height: '18px',
                                                borderRadius: '50%',
                                                backgroundColor: theme.palette.semantic.bd.error,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <MuiTypography
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    lineHeight: 1
                                                }}
                                            >
                                                !
                                            </MuiTypography>
                                        </Box>
                                    )}
                                    {isCurrentVersionDownloading ? (
                                        <DownloadInProgressIcon style={downloadInProgressIconStyle} />
                                    ) : (
                                        <FileDownloadIcon
                                            style={{
                                                cursor: 'pointer',
                                                color: theme.palette.primary[600] || '#1976d2'
                                            }}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                if (doc) {
                                                    loadClientToStore(Stage.reviewPlan);
                                                    setIsCurrentVersionDownloading(true);
                                                    setDrawerPdfDownloadFailed((prev) => {
                                                        if (!prev.has('current')) return prev;
                                                        const next = new Set(prev);
                                                        next.delete('current');
                                                        return next;
                                                    });
                                                    setPdfToast?.({ open: true, generating: true });
                                                    const toastHandler = (value: PdfDownloadToastState) => {
                                                        if (value.type === 'error') {
                                                            setPdfToast?.({ open: false, generating: false });
                                                            setDrawerPdfDownloadFailed((prev) =>
                                                                new Set(prev).add('current')
                                                            );
                                                            setShowDrawerToast(true);
                                                        } else {
                                                            setPdfToast?.(value);
                                                            if (value.type === 'confirm') {
                                                                setShowDrawerToast(false);
                                                            }
                                                        }
                                                    };
                                                    await downloadProposalVersionFile(
                                                        currentVersion,
                                                        proposalDocs?.length ?? 0,
                                                        toastHandler
                                                    );
                                                    setIsCurrentVersionDownloading(false);
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Previous Versions */}
                        {previousVersions?.map((versionDoc: any, index: number) => (
                            <Box
                                key={versionDoc?.id ?? index}
                                sx={{
                                    border: `1px solid ${theme.palette.semantic.bd.default}`,
                                    borderRadius: '8px',
                                    padding: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <MuiTypography
                                    variant="body1"
                                    sx={{ color: theme.palette.semantic.bg.brandDark, fontWeight: 600 }}
                                >
                                    {`v${(proposalDocs?.length ?? 0) - (index + 1)}`} &mdash;{' '}
                                    {`${formatTimestampToDateInParts(versionDoc?.timestamp).day} ${
                                        formatTimestampToDateInParts(versionDoc?.timestamp).month
                                    } ${formatTimestampToDateInParts(versionDoc?.timestamp).year}`}
                                </MuiTypography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {drawerPdfDownloadFailed.has(index) && (
                                        <Box
                                            sx={{
                                                width: '18px',
                                                height: '18px',
                                                borderRadius: '50%',
                                                backgroundColor: theme.palette.semantic.bd.error,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}
                                        >
                                            <MuiTypography
                                                sx={{ color: '#fff', fontSize: '14px', fontWeight: 700, lineHeight: 1 }}
                                            >
                                                !
                                            </MuiTypography>
                                        </Box>
                                    )}
                                    {isPreviousVersionDownloading === index ? (
                                        <DownloadInProgressIcon style={downloadInProgressIconStyle} />
                                    ) : (
                                        <FileDownloadIcon
                                            style={{ cursor: 'pointer', color: '#595959' }}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                {
                                                    setIsPreviousVersionDownloading(index);
                                                    setDrawerPdfDownloadFailed((prev) => {
                                                        if (!prev.has(index)) return prev;
                                                        const next = new Set(prev);
                                                        next.delete(index);
                                                        return next;
                                                    });
                                                    setPdfToast?.({ open: true, generating: true });
                                                    const toastHandler = (value: PdfDownloadToastState) => {
                                                        if (value.type === 'error') {
                                                            setPdfToast?.({ open: false, generating: false });
                                                            setDrawerPdfDownloadFailed((prev) =>
                                                                new Set(prev).add(index)
                                                            );
                                                            setShowDrawerToast(true);
                                                        } else {
                                                            setPdfToast?.(value);
                                                            if (value.type === 'confirm') {
                                                                setShowDrawerToast(false);
                                                            }
                                                        }
                                                    };
                                                    await downloadProposalVersionFile(
                                                        versionDoc,
                                                        (proposalDocs?.length ?? 0) - (index + 1),
                                                        toastHandler
                                                    );
                                                    setIsPreviousVersionDownloading(null);
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </DrawerComp>
        </ActionRequiredContext.Provider>
    );
};

export default Row;
